// engine.pattern.ts
// Pattern Compiler and Cache Engine for Tempo
// Responsible for snippet/layout expansion, regex compilation, and pattern caching
import { isRegExp, isNullish, isEmpty, isString } from '#library/assertion.library.js';
import { ownEntries, ownKeys } from '#library/primitive.library.js';
import { Match, Layout } from '../support/support.default.js';
import { getSymbol, hasOwn, logWarn, logError } from '../support/support.util.js';
import { Token } from '../support/support.symbol.js';
import enums from '../support/support.enum.js';
const BRACES_REGEX = new RegExp(Match.braces, 'g');
export class PatternCompiler {
    #state;
    #globalCache = new Map();
    #snippetCache = new WeakMap();
    constructor(options) {
        this.#state = options.state;
    }
    get state() { return this.#state; }
    /**
     * Translates {layout} into an anchored, case-insensitive RegExp.
     * Includes recursive expansion of placeholders using snippet registries.
     */
    compileRegExp(layout, snippet) {
        const state = this.#state;
        const source = isRegExp(layout) ? layout.source : layout;
        let cache;
        if (snippet) {
            if (!this.#snippetCache.has(snippet)) {
                this.#snippetCache.set(snippet, new Map());
            }
            cache = this.#snippetCache.get(snippet);
        }
        else {
            cache = this.#globalCache;
        }
        if (cache.has(source)) {
            return cache.get(source);
        }
        const matcher = (source, d = 0) => {
            if (d > 10) { // Emit a diagnostic if recursion limit is hit (likely circular placeholder)
                logWarn(`[PatternCompiler] Recursion limit exceeded in matcher (d > 10) for src:`, this.#state.config, source, `depth:`, d);
                return source;
            }
            if (source.startsWith('/') && source.endsWith('/'))
                source = source.substring(1, source.length - 1); // remove the leading/trailing "/"
            if (source.startsWith('^') && source.endsWith('$'))
                source = source.substring(1, source.length - 1); // remove the leading/trailing anchors (^ $)
            return source.replace(BRACES_REGEX, (match, name) => {
                const token = getSymbol(name); // get the symbol for this {name}
                const customs = snippet?.[token]?.source ?? snippet?.[name]?.source;
                const globals = state.parse.snippet[token]?.source ?? state.parse.snippet[name]?.source;
                const stateLayout = state.parse.layout[token] ?? state.parse.layout[name];
                const defaultLayout = Layout[token]; // get resolution source (layout)
                let res = customs ?? globals ?? stateLayout ?? defaultLayout; // get the snippet/layout source
                if (isNullish(res) && name.includes('.')) { // if no definition found, try fallback
                    const prefix = name.split('.')[0]; // get the base token name
                    const pToken = getSymbol(prefix);
                    res = snippet?.[pToken]?.source ?? snippet?.[prefix]?.source
                        ?? state.parse.snippet[pToken]?.source ?? state.parse.snippet[prefix]?.source
                        ?? state.parse.layout[pToken] ?? state.parse.layout[prefix]
                        ?? Layout[pToken];
                }
                if (res && name.includes('.')) { // wrap dotted extensions for identification
                    let safeName = name.trim().replace(/[^A-Za-z0-9_$]/g, '_');
                    if (!/^[A-Za-z_$]/.test(safeName))
                        safeName = `_${safeName}`;
                    if (!res.startsWith(`(?<${safeName}>`))
                        res = `(?<${safeName}>${res})`;
                }
                return (isNullish(res) || res === match) // if no definition found,
                    ? match // return the original match
                    : matcher(res, d + 1); // else recurse to see if snippet contains embedded "{}" pairs
            });
        };
        try {
            const expanded = matcher(source);
            const compiled = new RegExp(`^(${expanded})$`, 'i');
            cache.set(source, compiled);
            return compiled;
        }
        catch (e) {
            // Use the computed source for fallback, do not cache fallback, and log error
            logError(e, this.#state.config, { context: 'pattern compile failed', pattern: source });
            return new RegExp(`^${Match.escape(source)}$`, 'i');
        }
    }
    /**
     * Build RegExp patterns into the state.
     * Re-evaluates all snippets and layouts.
     */
    setPatterns() {
        this.clearCache();
        const state = this.#state;
        // ensure we have our own isolated mutable containers before mutation
        state.parse.snippet = { ...state.parse.snippet };
        state.parse.pattern = new Map();
        const snippet = state.parse.snippet;
        // 1. ensure numeric snippets are current
        if (enums?.NUMBER) {
            const keys = enums.NUMBER.keys().map(w => Match.escape(w));
            const nbr = new RegExp(`(?<nbr>[0-9]+|${keys.sort((a, b) => b.length - a.length).join('|')})`);
            snippet[Token.nbr] = nbr;
            let modPattern = Match.modifier.source;
            let afxPattern = Match.modifier.source;
            if (state.config.registry?.modifiers) {
                const symbols = ['+', '-', '<', '<=', '>', '>=', '='];
                const words = new Set();
                symbols.forEach(sym => {
                    const mapped = state.config.registry.modifiers[sym];
                    if (mapped)
                        (Array.isArray(mapped) ? mapped : [mapped]).forEach(w => words.add(w));
                });
                if (words.size > 0) {
                    const escapedWords = Array.from(words).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
                    escapedWords.sort((a, b) => b.length - a.length);
                    const wordPattern = escapedWords.join('|');
                    modPattern = `[\\+\\-\\<\\>][\\=]?|${wordPattern}`;
                    afxPattern = `[\\+\\-\\<\\>][\\=]?|${wordPattern}`;
                    const op = `[\\+\\-\\<\\>]=?`;
                    const terms = `(?:#[\\w]+|[\\w]+)`;
                    const units = `(?:[\\w]*)`;
                    snippet[Token.slk] = new RegExp(`(?:${terms}\\.(?:${op}|${wordPattern})?(?:[0-9]+)?${units})`, 'i');
                }
            }
            snippet[Token.mod] = new RegExp(`((?<mod>${modPattern})? *)`, 'i');
            snippet[Token.afx] = new RegExp(`((s)? (?<afx>${afxPattern}))?${snippet[Token.sep]?.source || ''}?`, 'i');
        }
        // 2. build ignore pattern
        const ignores = ownKeys(state.parse.ignore, true);
        if (!isEmpty(ignores)) {
            const words = ignores
                .filter(isString)
                .map(w => Match.escape(w.toLowerCase()))
                .join('|');
            state.parse.ignorePattern = new RegExp(`\\b(${words})\\b`, 'gi');
        }
        else {
            delete state.parse.ignorePattern;
        }
        // 3. build the patterns
        ownEntries(state.parse.layout, true).forEach(([key, layout]) => {
            const symbol = getSymbol(key);
            const compiled = this.compileRegExp(layout, snippet);
            state.parse.pattern.set(symbol, compiled);
        });
    }
    /**
     * Clear the pattern cache.
     */
    clearCache() {
        this.#globalCache.clear();
        // WeakMap has no clear(), so re-instantiate to drop all snippet-specific caches
        this.#snippetCache = new WeakMap();
    }
}
/**
 * Functional wrapper for the PatternCompiler.
 * Handles engine instantiation and pattern building for a given state.
 */
export function setPatterns(state) {
    // 🛡️ Critical fix: ensure we use an OWN PatternCompiler for each state to avoid cross-pollution
    if (!hasOwn(state, 'patternCompiler') || !state.patternCompiler)
        state.patternCompiler = new PatternCompiler({ state });
    state.patternCompiler.setPatterns();
}
